import { useState, useRef } from "react"
import { useQuery, useMutation } from "urql"
import { toast } from "sonner"
import { Download, Upload, Loader2, CheckCircle2 } from "lucide-react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { PageHeader } from "@/components/shared/page-header"
import { sectionTitleClass } from "@/components/shared/field-label"
import { ConfirmDialog } from "@/components/shared/confirm-dialog"
import {
  SourcesQuery,
  DestinationsQuery,
  SubscriptionsQuery,
  AlertRulesQuery,
  EventTypeSchemasQuery,
  TransformationsQuery,
  MetaEventConfigsQuery,
} from "@/lib/graphql/queries"
import {
  ApplicationsQuery,
  EndpointsQuery,
} from "@/lib/graphql/outbound"
import {
  CreateSourceMutation,
  CreateDestinationMutation,
  CreateSubscriptionMutation,
  CreateAlertRuleMutation,
  CreateEventTypeSchemaMutation,
  CreateTransformationMutation,
  CreateMetaEventConfigMutation,
} from "@/lib/graphql/mutations"
import {
  CreateApplicationMutation,
  CreateEndpointMutation,
} from "@/lib/graphql/outbound"
import type { ResultOf } from "@/lib/graphql/types"
import type {
  SourceStatus,
  DestinationType,
  AuthType,
  RetryPolicyInput,
  HealthConfigInput,
  ResponseConfigInput,
  DedupConfigInput,
  FilterConfigInput,
  AlertChannel,
  DeliveryMode,
} from "@/lib/generated/graphql"

type SourceNode = ResultOf<typeof SourcesQuery>["sources"]["nodes"][number]
type DestNode = ResultOf<typeof DestinationsQuery>["destinations"]["nodes"][number]
type SubNode = ResultOf<typeof SubscriptionsQuery>["subscriptions"]["nodes"][number]
type AppNode = ResultOf<typeof ApplicationsQuery>["applications"]["nodes"][number]
type EpNode = ResultOf<typeof EndpointsQuery>["endpoints"]["nodes"][number]
type AlertNode = ResultOf<typeof AlertRulesQuery>["alertRules"]["nodes"][number]
type TransformNode = ResultOf<typeof TransformationsQuery>["transformations"]["nodes"][number]
type SchemaNode = ResultOf<typeof EventTypeSchemasQuery>["eventTypeSchemas"]["nodes"][number]
type MetaEventNode = ResultOf<typeof MetaEventConfigsQuery>["metaEventConfigs"]["nodes"][number]

interface ExportData {
  version: string
  exportedAt: string
  sources: Array<Record<string, unknown>>
  destinations: Array<Record<string, unknown>>
  subscriptions: Array<Record<string, unknown>>
  applications?: Array<Record<string, unknown>>
  endpoints?: Array<Record<string, unknown>>
  alertRules?: Array<Record<string, unknown>>
  transformations?: Array<Record<string, unknown>>
  eventTypeSchemas?: Array<Record<string, unknown>>
  metaEventConfigs?: Array<Record<string, unknown>>
}

function buildExportData(
  sources: SourceNode[],
  destinations: DestNode[],
  subscriptions: SubNode[],
  applications: AppNode[],
  endpoints: EpNode[],
  alertRules: AlertNode[],
  transformations: TransformNode[],
  eventTypeSchemas: SchemaNode[],
  metaEventConfigs: MetaEventNode[],
): ExportData {
  return {
    version: "2",
    exportedAt: new Date().toISOString(),
    sources: sources.map((s) => ({
      name: s.name,
      slug: s.slug,
      providerType: s.providerType,
      verifyConfig: s.verifyConfig,
      status: s.status,
      rateLimitRps: s.rateLimitRps,
      spikeProtection: s.spikeProtection,
      maxIngestRps: s.maxIngestRps,
      brokerConfig: s.brokerConfig,
      responseConfig: s.responseConfig,
      dedupConfig: s.dedupConfig,
    })),
    destinations: destinations.map((d) => ({
      name: d.name,
      url: d.url,
      type: d.type,
      typeConfig: d.typeConfig,
      status: d.status,
      deliveryMode: d.deliveryMode,
      timeoutMs: d.timeoutMs,
      rateLimitRps: d.rateLimitRps,
      retryPolicy: d.retryPolicy,
      headers: d.headers,
      authType: d.authType,
      ordered: d.ordered,
      outputFormat: d.outputFormat,
      healthConfig: d.healthConfig,
    })),
    subscriptions: subscriptions.map((sub) => ({
      name: sub.name,
      sourceSlug: sub.source.slug,
      destinationName: sub.destination.name,
      filterConfig: sub.filterConfig,
      enabled: sub.enabled,
      delay: sub.delay,
      channels: sub.channels,
    })),
    applications: applications.map((a) => ({
      name: a.name,
      uid: a.uid,
    })),
    endpoints: endpoints.map((ep) => ({
      applicationUid: ep.application?.uid,
      url: ep.url,
      type: ep.type,
      typeConfig: ep.typeConfig,
      deliveryMode: ep.deliveryMode,
      status: ep.status,
      rateLimitRps: ep.rateLimitRps,
      timeoutMs: ep.timeoutMs,
      authType: ep.authType,
      ordered: ep.ordered,
      outputFormat: ep.outputFormat,
      healthConfig: ep.healthConfig,
      connectorType: ep.connectorType,
    })),
    alertRules: alertRules.map((r) => ({
      name: r.name,
      conditionType: r.conditionType,
      threshold: r.threshold,
      webhookUrl: r.webhookUrl,
      channel: r.channel,
      emailConfig: r.emailConfig,
      slackConfig: r.slackConfig,
      cooldown: r.cooldown,
      enabled: r.enabled,
    })),
    transformations: transformations.map((t) => ({
      name: t.name,
      description: t.description,
      code: t.code,
      enabled: t.enabled,
      failOpen: t.failOpen,
      timeoutMs: t.timeoutMs,
    })),
    eventTypeSchemas: eventTypeSchemas.map((s) => ({
      eventType: s.eventType,
      description: s.description,
      schema: s.schema,
      example: s.example,
    })),
    metaEventConfigs: metaEventConfigs.map((m) => ({
      name: m.name,
      url: m.url,
      eventTypes: m.eventTypes,
      enabled: m.enabled,
    })),
  }
}

interface ImportResult {
  sources: number
  destinations: number
  subscriptions: number
  applications: number
  endpoints: number
  alertRules: number
  transformations: number
  eventTypeSchemas: number
  metaEventConfigs: number
}

export function ConfigPage() {
  const [sources] = useQuery({ query: SourcesQuery, variables: { limit: 1000 } })
  const [destinations] = useQuery({ query: DestinationsQuery, variables: { limit: 1000 } })
  const [subscriptions] = useQuery({ query: SubscriptionsQuery, variables: { limit: 1000 } })
  const [applications] = useQuery({ query: ApplicationsQuery, variables: { limit: 1000 } })
  const [endpoints] = useQuery({ query: EndpointsQuery, variables: { limit: 1000 } })
  const [alertRules] = useQuery({ query: AlertRulesQuery, variables: { limit: 1000 } })
  const [transformations] = useQuery({ query: TransformationsQuery, variables: { limit: 1000 } })
  const [eventTypeSchemas] = useQuery({ query: EventTypeSchemasQuery, variables: { limit: 1000 } })
  const [metaEventConfigs] = useQuery({ query: MetaEventConfigsQuery, variables: { limit: 1000 } })

  const [, createSource] = useMutation(CreateSourceMutation)
  const [, createDest] = useMutation(CreateDestinationMutation)
  const [, createSub] = useMutation(CreateSubscriptionMutation)
  const [, createApp] = useMutation(CreateApplicationMutation)
  const [, createEndpoint] = useMutation(CreateEndpointMutation)
  const [, createAlertRule] = useMutation(CreateAlertRuleMutation)
  const [, createTransformation] = useMutation(CreateTransformationMutation)
  const [, createEventTypeSchema] = useMutation(CreateEventTypeSchemaMutation)
  const [, createMetaEventConfig] = useMutation(CreateMetaEventConfigMutation)

  const [importing, setImporting] = useState(false)
  const [importPreview, setImportPreview] = useState<ExportData | null>(null)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [importResult, setImportResult] = useState<ImportResult | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)

  const sourceNodes = sources.data?.sources.nodes ?? []
  const destNodes = destinations.data?.destinations.nodes ?? []
  const subNodes = subscriptions.data?.subscriptions.nodes ?? []
  const appNodes = applications.data?.applications.nodes ?? []
  const epNodes = endpoints.data?.endpoints.nodes ?? []
  const alertNodes = alertRules.data?.alertRules.nodes ?? []
  const transformNodes = transformations.data?.transformations.nodes ?? []
  const schemaNodes = eventTypeSchemas.data?.eventTypeSchemas.nodes ?? []
  const metaNodes = metaEventConfigs.data?.metaEventConfigs.nodes ?? []

  const handleExport = () => {
    const data = buildExportData(sourceNodes, destNodes, subNodes, appNodes, epNodes, alertNodes, transformNodes, schemaNodes, metaNodes)
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `hivehook-config-${new Date().toISOString().slice(0, 10)}.json`
    a.click()
    URL.revokeObjectURL(url)
    toast.success("Configuration exported")
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (ev) => {
      try {
        const data = JSON.parse(ev.target?.result as string) as ExportData
        if (!data.version || !data.sources || !data.destinations || !data.subscriptions) {
          toast.error("Invalid config file format")
          return
        }
        setImportPreview(data)
        setImportResult(null)
        setConfirmOpen(true)
      } catch {
        toast.error("Failed to parse JSON file")
      }
    }
    reader.readAsText(file)
    if (fileRef.current) fileRef.current.value = ""
  }

  const handleImport = async () => {
    if (!importPreview) return
    setImporting(true)
    setConfirmOpen(false)

    const result: ImportResult = {
      sources: 0,
      destinations: 0,
      subscriptions: 0,
      applications: 0,
      endpoints: 0,
      alertRules: 0,
      transformations: 0,
      eventTypeSchemas: 0,
      metaEventConfigs: 0,
    }

    const sourceIdBySlug = new Map<string, string>()
    const destIdByName = new Map<string, string>()
    const appIdByUid = new Map<string, string>()

    for (const s of importPreview.sources) {
      const slug = s.slug as string
      const existing = sourceNodes.find((n) => n.slug === slug)
      if (existing) {
        sourceIdBySlug.set(slug, existing.id)
        continue
      }
      const res = await createSource({
        input: {
          name: s.name as string,
          slug,
          providerType: s.providerType as string,
          verifyConfig: s.verifyConfig as Record<string, unknown>,
          status: s.status as SourceStatus,
          rateLimitRps: s.rateLimitRps as number,
          spikeProtection: s.spikeProtection as boolean,
          maxIngestRps: s.maxIngestRps as number,
          brokerConfig: s.brokerConfig as Record<string, unknown>,
          responseConfig: s.responseConfig as ResponseConfigInput,
          dedupConfig: s.dedupConfig as DedupConfigInput,
        },
      })
      if (res.error) {
        toast.error(`Failed to create source "${slug}": ${res.error.message}`)
      } else if (res.data?.createSource) {
        sourceIdBySlug.set(slug, res.data.createSource.id)
        result.sources++
      }
    }

    for (const d of importPreview.destinations) {
      const name = d.name as string
      const existing = destNodes.find((n) => n.name === name)
      if (existing) {
        destIdByName.set(name, existing.id)
        continue
      }
      const res = await createDest({
        input: {
          name,
          url: (d.url as string) || undefined,
          type: d.type as DestinationType,
          typeConfig: d.typeConfig as Record<string, unknown>,
          timeoutMs: d.timeoutMs as number,
          rateLimitRps: d.rateLimitRps as number,
          retryPolicy: d.retryPolicy as RetryPolicyInput,
          headers: d.headers as Record<string, unknown>,
          authType: d.authType as AuthType,
          ordered: d.ordered as boolean,
          outputFormat: d.outputFormat as string,
          healthConfig: d.healthConfig as HealthConfigInput,
        },
      })
      if (res.error) {
        toast.error(`Failed to create destination "${name}": ${res.error.message}`)
      } else if (res.data?.createDestination) {
        destIdByName.set(name, res.data.createDestination.id)
        result.destinations++
      }
    }

    for (const sub of importPreview.subscriptions) {
      const sourceSlug = sub.sourceSlug as string
      const destName = sub.destinationName as string
      const subName = sub.name as string
      const sourceId = sourceIdBySlug.get(sourceSlug)
      const destId = destIdByName.get(destName)
      if (!sourceId) {
        toast.error(`Source "${sourceSlug}" not found for subscription "${subName}"`)
        continue
      }
      if (!destId) {
        toast.error(`Destination "${destName}" not found for subscription "${subName}"`)
        continue
      }
      const res = await createSub({
        input: {
          name: subName,
          sourceId,
          destinationId: destId,
          filterConfig: sub.filterConfig as FilterConfigInput,
          delay: (sub.delay as string) || undefined,
          channels: (sub.channels as string[]) || undefined,
        },
      })
      if (res.error) {
        toast.error(`Failed to create subscription "${subName}": ${res.error.message}`)
      } else {
        result.subscriptions++
      }
    }

    for (const a of importPreview.applications ?? []) {
      const name = a.name as string
      const uid = a.uid as string
      const existing = appNodes.find((n) => n.uid === uid || n.name === name)
      if (existing) {
        appIdByUid.set(uid, existing.id)
        continue
      }
      const res = await createApp({ input: { name } })
      if (res.error) {
        toast.error(`Failed to create application "${name}": ${res.error.message}`)
      } else if (res.data?.createApplication) {
        appIdByUid.set(uid, res.data.createApplication.id)
        result.applications++
      }
    }

    for (const ep of importPreview.endpoints ?? []) {
      const appUid = ep.applicationUid as string
      const appId = appIdByUid.get(appUid)
      if (!appId) {
        toast.error(`Application "${appUid}" not found for endpoint`)
        continue
      }
      const res = await createEndpoint({
        input: {
          applicationId: appId,
          url: (ep.url as string) || undefined,
          type: ep.type as DestinationType,
          typeConfig: ep.typeConfig as Record<string, unknown>,
          deliveryMode: ep.deliveryMode as DeliveryMode,
          rateLimitRps: ep.rateLimitRps as number,
          timeoutMs: ep.timeoutMs as number,
          authType: ep.authType as AuthType,
          ordered: ep.ordered as boolean,
          outputFormat: ep.outputFormat as string,
          healthConfig: ep.healthConfig as HealthConfigInput,
          connectorType: (ep.connectorType as string) || undefined,
        },
      })
      if (res.error) {
        toast.error(`Failed to create endpoint: ${res.error.message}`)
      } else {
        result.endpoints++
      }
    }

    for (const r of importPreview.alertRules ?? []) {
      const name = r.name as string
      const existing = alertNodes.find((n) => n.name === name)
      if (existing) continue
      const res = await createAlertRule({
        input: {
          name,
          conditionType: r.conditionType as string,
          threshold: r.threshold as number,
          webhookUrl: (r.webhookUrl as string) || undefined,
          channel: r.channel as AlertChannel,
          emailConfig: r.emailConfig as { to: string[]; subjectTemplate?: string },
          slackConfig: r.slackConfig as { webhookUrl: string; channel?: string },
          cooldown: (r.cooldown as string) || undefined,
          enabled: r.enabled as boolean,
        },
      })
      if (res.error) {
        toast.error(`Failed to create alert rule "${name}": ${res.error.message}`)
      } else {
        result.alertRules++
      }
    }

    for (const t of importPreview.transformations ?? []) {
      const name = t.name as string
      const existing = transformNodes.find((n) => n.name === name)
      if (existing) continue
      const res = await createTransformation({
        input: {
          name,
          description: (t.description as string) || undefined,
          code: t.code as string,
          failOpen: t.failOpen as boolean,
          timeoutMs: t.timeoutMs as number,
        },
      })
      if (res.error) {
        toast.error(`Failed to create transformation "${name}": ${res.error.message}`)
      } else {
        result.transformations++
      }
    }

    for (const s of importPreview.eventTypeSchemas ?? []) {
      const eventType = s.eventType as string
      const existing = schemaNodes.find((n) => n.eventType === eventType)
      if (existing) continue
      const res = await createEventTypeSchema({
        input: {
          eventType,
          description: (s.description as string) || undefined,
          schema: s.schema as Record<string, unknown>,
          example: s.example as Record<string, unknown>,
        },
      })
      if (res.error) {
        toast.error(`Failed to create schema "${eventType}": ${res.error.message}`)
      } else {
        result.eventTypeSchemas++
      }
    }

    for (const m of importPreview.metaEventConfigs ?? []) {
      const name = m.name as string
      const existing = metaNodes.find((n) => n.name === name)
      if (existing) continue
      const res = await createMetaEventConfig({
        input: {
          name,
          url: m.url as string,
          eventTypes: m.eventTypes as string[],
          enabled: m.enabled as boolean,
        },
      })
      if (res.error) {
        toast.error(`Failed to create meta event config "${name}": ${res.error.message}`)
      } else {
        result.metaEventConfigs++
      }
    }

    setImporting(false)
    setImportResult(result)
    const total = result.sources + result.destinations + result.subscriptions + result.applications + result.endpoints + result.alertRules + result.transformations + result.eventTypeSchemas + result.metaEventConfigs
    toast.success(`Imported ${total} resources`)
  }

  const loading = sources.fetching || destinations.fetching || subscriptions.fetching || applications.fetching || endpoints.fetching || alertRules.fetching || transformations.fetching || eventTypeSchemas.fetching || metaEventConfigs.fetching

  const previewCounts = importPreview
    ? [
        importPreview.sources.length > 0 && `${importPreview.sources.length} sources`,
        importPreview.destinations.length > 0 && `${importPreview.destinations.length} destinations`,
        importPreview.subscriptions.length > 0 && `${importPreview.subscriptions.length} subscriptions`,
        (importPreview.applications?.length ?? 0) > 0 && `${importPreview.applications!.length} applications`,
        (importPreview.endpoints?.length ?? 0) > 0 && `${importPreview.endpoints!.length} endpoints`,
        (importPreview.alertRules?.length ?? 0) > 0 && `${importPreview.alertRules!.length} alert rules`,
        (importPreview.transformations?.length ?? 0) > 0 && `${importPreview.transformations!.length} transformations`,
        (importPreview.eventTypeSchemas?.length ?? 0) > 0 && `${importPreview.eventTypeSchemas!.length} event type schemas`,
        (importPreview.metaEventConfigs?.length ?? 0) > 0 && `${importPreview.metaEventConfigs!.length} meta event configs`,
      ].filter(Boolean).join(", ")
    : ""

  return (
    <div className="space-y-6">
      <PageHeader title="Configuration" description="Export and import your full configuration" />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-border/30">
          <CardHeader>
            <CardTitle className={sectionTitleClass}>Export</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Download your current configuration as a JSON file. Includes all resources.
            </p>
            <div className="flex flex-wrap gap-2">
              <Badge variant="outline" className="text-2xs">{sourceNodes.length} sources</Badge>
              <Badge variant="outline" className="text-2xs">{destNodes.length} destinations</Badge>
              <Badge variant="outline" className="text-2xs">{subNodes.length} subscriptions</Badge>
              <Badge variant="outline" className="text-2xs">{appNodes.length} applications</Badge>
              <Badge variant="outline" className="text-2xs">{epNodes.length} endpoints</Badge>
              <Badge variant="outline" className="text-2xs">{alertNodes.length} alert rules</Badge>
              <Badge variant="outline" className="text-2xs">{transformNodes.length} transformations</Badge>
              <Badge variant="outline" className="text-2xs">{schemaNodes.length} event types</Badge>
              <Badge variant="outline" className="text-2xs">{metaNodes.length} meta events</Badge>
            </div>
            <Button onClick={handleExport} disabled={loading} className="w-full">
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              Export Configuration
            </Button>
          </CardContent>
        </Card>

        <Card className="border-border/30">
          <CardHeader>
            <CardTitle className={sectionTitleClass}>Import</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Import configuration from a JSON file. Existing resources with matching slugs/names will be skipped.
            </p>
            {importResult && (
              <div className="flex items-start gap-2 rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3">
                <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" />
                <span className="text-sm">
                  Created {importResult.sources} sources, {importResult.destinations} destinations, {importResult.subscriptions} subscriptions
                  {importResult.applications > 0 && `, ${importResult.applications} applications`}
                  {importResult.endpoints > 0 && `, ${importResult.endpoints} endpoints`}
                  {importResult.alertRules > 0 && `, ${importResult.alertRules} alert rules`}
                  {importResult.transformations > 0 && `, ${importResult.transformations} transformations`}
                  {importResult.eventTypeSchemas > 0 && `, ${importResult.eventTypeSchemas} event type schemas`}
                  {importResult.metaEventConfigs > 0 && `, ${importResult.metaEventConfigs} meta event configs`}
                </span>
              </div>
            )}
            <input
              ref={fileRef}
              type="file"
              accept=".json,application/json"
              className="hidden"
              onChange={handleFileSelect}
            />
            <Button
              variant="outline"
              onClick={() => fileRef.current?.click()}
              disabled={importing}
              className="w-full"
            >
              {importing ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Upload className="h-4 w-4 mr-2" />
              )}
              {importing ? "Importing..." : "Select JSON File"}
            </Button>
          </CardContent>
        </Card>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Import Configuration"
        description={importPreview ? `This will import: ${previewCounts}. Existing resources with matching slugs/names will be skipped.` : ""}
        confirmLabel="Import"
        onConfirm={handleImport}
        loading={importing}
      />
    </div>
  )
}
